import { useCallback, useEffect, useState } from "react";
import {
  fetchHospitalMedicalActs,
  type HospitalMedicalActApi,
} from "../api/hospitalApi";
import { getAuthToken } from "../auth";
import { providerMedicalActs } from "../data";
import type { ProviderMedicalAct, ProviderMedicalActCareType } from "../types";

function mapCareType(value: string): ProviderMedicalActCareType {
  if (
    value === "pharmacy" ||
    value === "nursing" ||
    value === "surgery" ||
    value === "consultation" ||
    value === "imagingLab"
  ) {
    return value;
  }
  return "consultation";
}

function formatCreatedAt(value?: string): string {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString("fr-FR");
}

function mapApiAct(row: HospitalMedicalActApi): ProviderMedicalAct {
  return {
    id: String(row.id),
    code: row.code,
    label: row.label || row.libelle,
    careType: mapCareType(row.care_type),
    costUsd: Number(row.cost_usd ?? row.cost ?? 0),
    createdAt: formatCreatedAt(row.created_at),
    pendingValidation: Boolean(row.pending_validation),
  };
}

export function useHospitalMedicalActs() {
  const [acts, setActs] = useState<ProviderMedicalAct[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [usingApi, setUsingApi] = useState(false);

  const load = useCallback(async () => {
    if (!getAuthToken()) {
      setUsingApi(false);
      setActs(providerMedicalActs);
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);
    setUsingApi(true);

    try {
      const api = await fetchHospitalMedicalActs();
      setActs((api.medical_acts ?? []).map(mapApiAct));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Impossible de charger les actes.");
      setActs(providerMedicalActs);
      setUsingApi(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  return {
    acts,
    setActs,
    loading,
    error,
    usingApi,
    reload: load,
  };
}
